import { ApplicationInsights, SeverityLevel } from "@microsoft/applicationinsights-web";

let appInsights = null;
let telemetryReady = false;

const parseBoolean = (value, defaultValue = false) => {
  if (value === undefined || value === null || value === "") {
    return defaultValue;
  }

  return String(value).toLowerCase() === "true";
};

const parseNumber = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const getConnectionString = () =>
  process.env.REACT_APP_APPLICATIONINSIGHTS_CONNECTION_STRING?.trim() || "";

export const initializeBrowserTelemetry = () => {
  if (telemetryReady) {
    return appInsights;
  }

  telemetryReady = true;

  const enabled = parseBoolean(process.env.REACT_APP_ENABLE_APPLICATION_INSIGHTS, true);
  const connectionString = getConnectionString();

  if (!enabled || !connectionString) {
    return null;
  }

  appInsights = new ApplicationInsights({
    config: {
      connectionString,
      enableAutoRouteTracking: false,
      autoTrackPageVisitTime: true,
      disableAjaxTracking: false,
      disableFetchTracking: false,
      enableCorsCorrelation: true,
      samplingPercentage: parseNumber(
        process.env.REACT_APP_APPLICATIONINSIGHTS_SAMPLING_PERCENTAGE,
        100
      ),
      maxAjaxCallsPerView: 500,
    },
  });

  appInsights.loadAppInsights();

  if (typeof window !== "undefined") {
    window.addEventListener("error", (event) => {
      if (event?.error) {
        trackException(event.error, {
          source: "window.error",
          filename: event.filename || "",
          lineno: String(event.lineno || ""),
          colno: String(event.colno || ""),
        });
      }
    });

    window.addEventListener("unhandledrejection", (event) => {
      const reason = event?.reason instanceof Error ? event.reason : new Error(String(event?.reason || "Unhandled rejection"));
      trackException(reason, {
        source: "window.unhandledrejection",
      });
    });
  }

  return appInsights;
};

export const getAppInsights = () => appInsights;

export const trackPageView = (pageName, uri) => {
  if (!appInsights) return;
  appInsights.trackPageView({ name: pageName, uri });
};

export const trackEvent = (name, properties = {}) => {
  if (!appInsights) return;
  appInsights.trackEvent({ name }, properties);
};

export const trackMetric = (name, average, properties = {}) => {
  if (!appInsights) return;
  appInsights.trackMetric({ name, average }, properties);
};

export const trackException = (error, properties = {}) => {
  if (!appInsights) return;

  appInsights.trackException({
    error,
    severityLevel: SeverityLevel.Error,
    properties,
  });
};

export const trackWarning = (message, properties = {}) => {
  if (!appInsights) return;
  appInsights.trackTrace({
    message,
    severityLevel: SeverityLevel.Warning,
    properties,
  });
};

export const getClientRequestId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `req-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const trackWebVital = (metric) => {
  if (!metric) return;

  trackMetric(metric.name, metric.value, {
    id: metric.id,
    delta: String(metric.delta),
  });
};
